import { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * The Threshold — universal gate. Every visit to T.I.V. Nexus passes through
 * here first. A bare sigil, a quiet line of instruction, and a key prompt.
 * Once you've identified yourself you're admitted to the rest of the archive.
 */
export default function Threshold() {
  const { login, intruderForce, isAuthenticated, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [sigilClicks, setSigilClicks] = useState(0);

  const redirectTo = location.state?.from || "/";

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setLocalError("");
    const result = await login(username.trim(), password);
    setLoading(false);
    if (result) {
      navigate(redirectTo, { replace: true });
    } else {
      setLocalError("The threshold rejects you. The vault remains shut.");
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  // Secret trapdoor: 7 successive clicks on the sigil invoke the Intruder
  // Force entry. Resets after 2 seconds of inactivity so casual fidgeting
  // doesn't trigger it. Only succeeds if an administrator has flipped the
  // `intruder_force_enabled` flag in the Admin Console — otherwise the
  // backend returns 403 and we surface a quiet rejection.
  const onSigilClick = async () => {
    const next = sigilClicks + 1;
    setSigilClicks(next);
    if (next < 7) {
      window.clearTimeout(window.__tivSigilTimer);
      window.__tivSigilTimer = window.setTimeout(() => setSigilClicks(0), 2000);
      return;
    }
    // 7th click — try the trapdoor
    setSigilClicks(0);
    setLocalError("");
    if (loading) return;
    setLoading(true);
    const result = await intruderForce();
    setLoading(false);
    if (result) {
      navigate(redirectTo, { replace: true });
    } else {
      // backend already surfaced a 403 → error is in `error` state via context;
      // also show a quiet local hint
      setLocalError("The trapdoor is sealed.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#050505] text-[#E3E1D9] flex items-center justify-center overflow-y-auto"
      data-testid="threshold-page"
    >
      <div className="grain-overlay" />
      <div className="scan-lines" />

      <div className="relative w-full max-w-md mx-6 my-12 text-center space-y-10">
        <div className="space-y-4">
          <div className="text-[0.55rem] tracking-[0.45em] uppercase text-[#721C1C]">
            Terra Infernalis Via — Threshold
          </div>
          <div
            className="font-serif-display text-[7rem] leading-none select-none tiv-blink cursor-pointer"
            aria-hidden
            onClick={onSigilClick}
            data-testid="threshold-sigil"
          >
            ◌
          </div>
          <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#555]">
            Septem · Profunda · Custos
          </div>
        </div>

        <p className="text-xs text-[#A3A3A3] leading-relaxed px-4">
          What lies beyond is not entertainment. It is a record. Names struck
          through. Doors that were never meant to be opened. Present your key.
        </p>

        {!revealed ? (
          <button
            type="button"
            className="tiv-btn w-full"
            onClick={() => setRevealed(true)}
            data-testid="threshold-reveal-btn"
          >
            Present the Key
          </button>
        ) : (
          <div className="space-y-4 text-left" data-testid="threshold-form" onKeyDown={onKeyDown}>
            <label className="block">
              <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">
                File / Handle
              </div>
              <input
                type="text"
                className="tiv-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="A-000 · Intern-417 · ..."
                autoComplete="username"
                autoFocus
                data-testid="threshold-username"
              />
            </label>
            <label className="block">
              <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">
                Alias
              </div>
              <input
                type="password"
                className="tiv-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="threshold-password"
              />
            </label>
            {(localError || error) && (
              <div
                className="text-xs text-[#721C1C] border border-[#721C1C] p-2 tracking-wide"
                data-testid="threshold-error"
              >
                {localError || error}
              </div>
            )}
            <button
              className="tiv-btn w-full"
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              data-testid="threshold-submit"
            >
              {loading ? "verifying..." : "Cross the Threshold"}
            </button>
          </div>
        )}

        <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#333] pt-4 border-t border-[#1a1a1a]">
          MMXXVI — Per silentium, custos.
        </div>
      </div>
    </div>
  );
}
